import { Button, Modal } from '@dev-spendesk/grapes';
import { type FormikErrors, useFormik } from 'formik';
import { useMemo, useState } from 'react';

import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { useApprovalWorkflowHasNodeWithCostCenter } from 'src/core/modules/approval-by-dimensions/hooks/useApprovalWorkflowQuery';

import { CostCenterDeletionDetails } from './CostCenterDeletionDetails';
import {
  type CostCenterReplacePreview,
  isCostCenterReplacementRequired,
} from './costCenterReplacePreview';
import type { FormValues } from './formValues';
import { type CostCenter } from '../../costCenter';

type Props = {
  costCenter: {
    id: string;
    name: string;
    hasBudgets: boolean;
    replacePreview: CostCenterReplacePreview;
  };
  isOpen: boolean;
  substituteCostCenters: CostCenter[];
  onComplete(costCenterId?: string): void;
  onCancel(): void;
  isCostCenterMapped?: boolean;
};

export const CostCenterDeletionModal = ({
  costCenter,
  substituteCostCenters,
  isOpen,
  onCancel,
  onComplete,
  isCostCenterMapped,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const isReplacementRequired =
    substituteCostCenters.length > 0 &&
    isCostCenterReplacementRequired(costCenter.replacePreview);
  const hasApprovalWorkflowNodeWithCostCenter =
    useApprovalWorkflowHasNodeWithCostCenter({
      costCenterId: costCenter.id,
    });
  const [shouldShowSecondModal, setShouldShowSecondModal] = useState(false);

  const formikProps = useFormik<FormValues>({
    initialValues: {
      costCenterId: undefined,
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.costCenterId) {
        errors.costCenterId = t('costCenters.deletion.noCostCenterSelected');
      }
      return errors;
    },
    onSubmit: (values) => {
      onComplete(values.costCenterId);
    },
  });

  const handleSubmit = () => {
    if (isReplacementRequired) {
      formikProps.submitForm();
      return;
    }
    onComplete();
  };

  const handleOnNext = () => {
    if (isReplacementRequired) {
      formikProps.submitForm();
    }
    setShouldShowSecondModal(true);
  };

  const hasSecondAction = useMemo(() => {
    return hasApprovalWorkflowNodeWithCostCenter && isReplacementRequired;
  }, [hasApprovalWorkflowNodeWithCostCenter, isReplacementRequired]);

  const hasAttachedEntities = useMemo(() => {
    return hasCostCenterAttachedEntities(costCenter);
  }, [costCenter]);

  return (
    <Modal
      isOpen={isOpen}
      iconName="hexagone-cross"
      iconVariant="alert"
      title={t('costCenters.deletion.deleteModalTitle', {
        costCenterName: costCenter.name,
      })}
      subtitle={getSubtitle({
        hasAttachedEntities,
        t,
        costCenterName: costCenter.name,
        hasApprovalWorkflowNodeWithCostCenter,
        shouldShowSecondModal,
        isCostCenterMapped,
        integration: accountingSoftwareName,
      })}
      actions={[
        <Button
          key="1"
          variant="secondaryNeutral"
          text={t('costCenters.deletion.cancelButton')}
          onClick={onCancel}
        />,
        <Button
          key="2"
          variant="primaryAlert"
          text={t('costCenters.deletion.delete')}
          onClick={hasSecondAction ? handleOnNext : handleSubmit}
        />,
      ]}
    >
      {hasAttachedEntities && !shouldShowSecondModal && (
        <div className="mt-8">
          <CostCenterDeletionDetails
            {...formikProps}
            costCenter={costCenter}
            isReplacementRequired={isReplacementRequired}
            substituteCostCenters={substituteCostCenters}
          />
        </div>
      )}
    </Modal>
  );
};

const getSubtitle = ({
  hasAttachedEntities,
  t,
  costCenterName,
  hasApprovalWorkflowNodeWithCostCenter,
  shouldShowSecondModal,
  isCostCenterMapped,
  integration,
}: {
  hasAttachedEntities: boolean;
  t: TGlobalFunctionTyped;
  costCenterName: string;
  hasApprovalWorkflowNodeWithCostCenter: boolean;
  shouldShowSecondModal: boolean;
  isCostCenterMapped?: boolean;
  integration?: string;
}) => {
  if (isCostCenterMapped) {
    return t(
      'bookkeep.integrations.settings.analyticalFieldMapping.deleteCostCenterValue',
      { integration },
    );
  }

  if (hasAttachedEntities && !shouldShowSecondModal) {
    return t('costCenters.deletion.modalSubTitleSubmission', {
      costCenterName,
    });
  }
  if (hasApprovalWorkflowNodeWithCostCenter) {
    return t('costCenters.deletion.deleteConfirmationWithApprovalWorkflow', {
      costCenterName,
    });
  }
  return t('costCenters.deletion.modalSubTitleSubmissionNoEntities', {
    costCenterName,
  });
};
const hasCostCenterAttachedEntities = (
  costCenter: Props['costCenter'],
): boolean => {
  const attachedEntities = Object.values(costCenter.replacePreview).reduce(
    (sum, counter) => sum + counter,
    0,
  );
  return attachedEntities !== 0;
};
